/**
 * AuthModal — A beautiful slide-up modal containing the full login/signup/forgot-password
 * flow. Used when a guest tries to access a protected action (e.g. checkout).
 *
 * Props:
 *   open       — controls visibility
 *   onClose    — called when the user explicitly dismisses the modal
 *   onSuccess  — called after a successful login or signup
 *   defaultTab — which tab to show first (default: "login")
 */
import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  login,
  requestPasswordReset,
  signup,
  verifySignupOtp,
  verifyPasswordResetOtp,
  resetPasswordWithToken,
} from "@/lib/api/auth";
import { useApp } from "@/contexts/AppContext";
import { X } from "lucide-react";

type Tab = "login" | "signup" | "forgot" | "forgot-otp" | "forgot-new";
const TABS: Tab[] = ["login", "signup", "forgot"];

/* ─── Shared style tokens (same as Auth page) ─── */
const inputCls =
  "w-full px-4 py-3.5 rounded-xl border text-body-md outline-none transition-all duration-300 " +
  "bg-surface-container-lowest border-outline-variant/50 text-on-surface placeholder:text-outline-variant/60 " +
  "focus:border-golden-latte focus:bg-surface-container-lowest focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-golden-latte)_15%,transparent)]";
const labelCls =
  "block text-[10px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant mb-1.5";
const btnCls =
  "relative w-full py-3.5 rounded-xl text-label-md font-label-md uppercase tracking-[0.16em] transition-all duration-300 " +
  "bg-primary text-white " +
  "hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-primary)_60%,transparent)] hover:scale-[1.01] active:scale-[0.99] " +
  "disabled:opacity-60 disabled:hover:scale-100 disabled:hover:shadow-none";
const ghostBtnCls =
  "w-full py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-on-surface-variant " +
  "hover:text-primary transition-colors duration-200";

function Field({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Panel({
  id,
  dir,
  children,
}: {
  id: string;
  dir: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, x: dir * 30, filter: "blur(8px)" }}
      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, x: dir * -30, filter: "blur(8px)" }}
      transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultTab?: "login" | "signup";
}

export function AuthModal({
  open,
  onClose,
  onSuccess,
  defaultTab = "login",
}: AuthModalProps) {
  const { t } = useTranslation();
  const { refreshAuth } = useApp();
  const [tab, setTab] = useState<Tab>(defaultTab);
  const dir = useRef(1);
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  // Reset to defaultTab when modal opens
  useEffect(() => {
    if (open) {
      setTab(defaultTab);
      setOtpEmail(null);
      setOtpCode("");
    }
  }, [open, defaultTab]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (next: Tab) => {
    dir.current = TABS.indexOf(next) >= TABS.indexOf(tab) ? 1 : -1;
    setTab(next);
  };

  const tabKey = tab === "signup" && otpEmail ? "otp" : tab;

  async function doLogin(e: React.FormEvent) {
    e.preventDefault();
    setPending("login");
    try {
      const u = await login(loginForm);
      await refreshAuth();
      toast.success(t("auth.welcomeToast", { name: u.firstName }));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  async function doSignup(e: React.FormEvent) {
    e.preventDefault();
    setPending("signup");
    try {
      const r = await signup(signupForm);
      if (r.otpRequired) {
        setOtpEmail(r.email);
        toast.success(t("auth.otpSent", { email: r.email }));
        return;
      }
      await refreshAuth();
      toast.success(t("auth.accountReady"));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  async function doVerify(e: React.FormEvent) {
    e.preventDefault();
    setPending("verify");
    try {
      await verifySignupOtp({ email: otpEmail!, code: otpCode });
      await refreshAuth();
      toast.success(t("auth.accountReady"));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  async function doReset(e: React.FormEvent) {
    e.preventDefault();
    setPending("reset");
    try {
      await requestPasswordReset({ email: resetEmail });
      toast.success(t("auth.otpSent", { email: resetEmail }));
      go("forgot-otp");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  async function doVerifyResetOtp(e: React.FormEvent) {
    e.preventDefault();
    setPending("verify-reset");
    try {
      const r = await verifyPasswordResetOtp({ email: resetEmail, code: resetOtp });
      setResetToken((r as any).token ?? "");
      go("forgot-new");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  async function doResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending("reset-submit");
    try {
      await resetPasswordWithToken({ token: resetToken, password: newPassword });
      toast.success(t("auth.passwordChanged") || "Password changed successfully!");
      go("login");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="auth-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal panel — slides up from bottom on mobile, centered on desktop */}
          <motion.div
            key="auth-modal-panel"
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-50 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-6 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full sm:max-w-[460px] rounded-t-3xl sm:rounded-2xl overflow-hidden bg-surface-container-lowest shadow-2xl border border-outline-variant/30 max-h-[92dvh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <div className="flex items-center justify-between px-6 pt-5 pb-0">
                <h2 className="text-headline-sm font-headline-sm text-primary">SEEN</h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-2 hover:bg-surface-container transition-colors text-on-surface-variant"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Tab switcher */}
              <div className="px-6 pt-4 pb-0">
                <div className="flex gap-1 p-1 rounded-xl bg-surface-container-low border border-outline-variant/30">
                  {(["login", "signup"] as Tab[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => go(key)}
                      className={`relative flex-1 py-2.5 rounded-lg text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-200 focus:outline-none ${
                        tab === key || (tab === "forgot" && key === "login")
                          ? "text-primary"
                          : "text-on-surface-variant hover:text-primary/70"
                      }`}
                    >
                      {(tab === key || (tab === "forgot" && key === "login")) && (
                        <motion.span
                          layoutId="auth-modal-pill"
                          className="absolute inset-0 rounded-lg bg-surface-container-lowest shadow-sm"
                          transition={{ type: "spring", stiffness: 500, damping: 38 }}
                        />
                      )}
                      <span className="relative z-10">{t(`auth.${key}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form area */}
              <div className="px-6 pt-6 pb-8">
                <AnimatePresence mode="wait" initial={false}>
                  {/* ── LOGIN ── */}
                  {tabKey === "login" && (
                    <Panel id="m-login" dir={dir.current}>
                      <form onSubmit={doLogin} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.welcome")}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.welcomeDesc")}
                          </p>
                        </div>
                        <div className="space-y-4">
                          <Field label={t("auth.email")} id="ml-email">
                            <input
                              id="ml-email"
                              type="email"
                              autoComplete="email"
                              placeholder={t("auth.placeholders.email")}
                              className={inputCls}
                              value={loginForm.email}
                              onChange={(e) =>
                                setLoginForm((p) => ({ ...p, email: e.target.value }))
                              }
                            />
                          </Field>
                          <Field label={t("auth.password")} id="ml-pw">
                            <input
                              id="ml-pw"
                              type="password"
                              autoComplete="current-password"
                              placeholder={t("auth.placeholders.password")}
                              className={inputCls}
                              value={loginForm.password}
                              onChange={(e) =>
                                setLoginForm((p) => ({ ...p, password: e.target.value }))
                              }
                            />
                          </Field>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => go("forgot")}
                            className="text-[11px] text-primary/70 hover:text-primary transition-colors tracking-wide"
                          >
                            {t("auth.forgot")} {t("auth.password")}?
                          </button>
                        </div>
                        <button
                          type="submit"
                          disabled={pending === "login"}
                          className={btnCls}
                        >
                          {pending === "login" ? t("auth.signingIn") : t("auth.signIn")}
                        </button>
                        <p className="text-center text-[11px] text-on-surface-variant">
                          {t("auth.noAccount")}{" "}
                          <button
                            type="button"
                            onClick={() => go("signup")}
                            className="text-primary hover:text-golden-latte transition-colors font-semibold"
                          >
                            {t("auth.registerNow")}
                          </button>
                        </p>
                      </form>
                    </Panel>
                  )}

                  {/* ── SIGN UP ── */}
                  {tabKey === "signup" && (
                    <Panel id="m-signup" dir={dir.current}>
                      <form onSubmit={doSignup} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.joinTitle")}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.joinDesc")}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t("auth.firstName")} id="ms-fn">
                            <input
                              id="ms-fn"
                              type="text"
                              placeholder={t("auth.placeholders.firstName")}
                              className={inputCls}
                              value={signupForm.firstName}
                              onChange={(e) =>
                                setSignupForm((p) => ({ ...p, firstName: e.target.value }))
                              }
                            />
                          </Field>
                          <Field label={t("auth.lastName")} id="ms-ln">
                            <input
                              id="ms-ln"
                              type="text"
                              placeholder={t("auth.placeholders.lastName")}
                              className={inputCls}
                              value={signupForm.lastName}
                              onChange={(e) =>
                                setSignupForm((p) => ({ ...p, lastName: e.target.value }))
                              }
                            />
                          </Field>
                        </div>
                        <Field label={t("auth.email")} id="ms-email">
                          <input
                            id="ms-email"
                            type="email"
                            autoComplete="email"
                            placeholder={t("auth.placeholders.signupEmail")}
                            className={inputCls}
                            value={signupForm.email}
                            onChange={(e) =>
                              setSignupForm((p) => ({ ...p, email: e.target.value }))
                            }
                          />
                        </Field>
                        <Field label={t("auth.password")} id="ms-pw">
                          <input
                            id="ms-pw"
                            type="password"
                            autoComplete="new-password"
                            placeholder={t("auth.placeholders.createPassword")}
                            className={inputCls}
                            value={signupForm.password}
                            onChange={(e) =>
                              setSignupForm((p) => ({ ...p, password: e.target.value }))
                            }
                          />
                        </Field>
                        <button
                          type="submit"
                          disabled={pending === "signup"}
                          className={btnCls}
                        >
                          {pending === "signup"
                            ? t("auth.creating")
                            : t("auth.createAccount")}
                        </button>
                        <p className="text-center text-[10px] text-on-surface-variant/70 leading-relaxed max-w-[280px] mx-auto">
                          {t("auth.legal")}{" "}
                          <a
                            href="#"
                            className="text-on-surface-variant hover:text-primary underline underline-offset-2"
                          >
                            {t("auth.privacy")}
                          </a>{" "}
                          {t("auth.and")}{" "}
                          <a
                            href="#"
                            className="text-on-surface-variant hover:text-primary underline underline-offset-2"
                          >
                            {t("auth.terms")}
                          </a>
                          .
                        </p>
                      </form>
                    </Panel>
                  )}

                  {/* ── OTP ── */}
                  {tabKey === "otp" && (
                    <Panel id="m-otp" dir={1}>
                      <form onSubmit={doVerify} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.otpTitle")}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.otpDesc", { email: otpEmail })}
                          </p>
                        </div>
                        <Field label={t("auth.otpCode")} id="m-otp-c">
                          <input
                            id="m-otp-c"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="• • • • • •"
                            className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold py-6`}
                            value={otpCode}
                            onChange={(e) =>
                              setOtpCode(e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </Field>
                        <button
                          type="submit"
                          disabled={pending === "verify" || otpCode.length < 4}
                          className={btnCls}
                        >
                          {pending === "verify" ? t("auth.verifying") : t("auth.verify")}
                        </button>
                        <button
                          type="button"
                          onClick={doSignup as any}
                          disabled={pending === "signup"}
                          className={ghostBtnCls}
                        >
                          {t("auth.otpResend")}
                        </button>
                      </form>
                    </Panel>
                  )}

                  {/* ── FORGOT PASSWORD ── */}
                  {tabKey === "forgot" && (
                    <Panel id="m-forgot" dir={dir.current}>
                      <form onSubmit={doReset} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.resetTitle")}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.resetDesc") ||
                              "Enter your email and we'll send a recovery OTP."}
                          </p>
                        </div>
                        <Field label={t("auth.email")} id="mr-email">
                          <input
                            id="mr-email"
                            type="email"
                            placeholder={t("auth.placeholders.email")}
                            className={inputCls}
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                          />
                        </Field>
                        <button
                          type="submit"
                          disabled={pending === "reset"}
                          className={btnCls}
                        >
                          {pending === "reset"
                            ? t("auth.sending")
                            : t("auth.sendOtp") || "Send OTP"}
                        </button>
                        <button
                          type="button"
                          onClick={() => go("login")}
                          className={ghostBtnCls}
                        >
                          ← {t("auth.backToLogin")}
                        </button>
                      </form>
                    </Panel>
                  )}

                  {/* ── FORGOT OTP ── */}
                  {tabKey === "forgot-otp" && (
                    <Panel id="m-forgot-otp" dir={1}>
                      <form onSubmit={doVerifyResetOtp} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.otpTitle")}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.otpDesc", { email: resetEmail })}
                          </p>
                        </div>
                        <Field label={t("auth.otpCode")} id="mr-otp-c">
                          <input
                            id="mr-otp-c"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="• • • • • •"
                            className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold py-6`}
                            value={resetOtp}
                            onChange={(e) =>
                              setResetOtp(e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </Field>
                        <button
                          type="submit"
                          disabled={pending === "verify-reset" || resetOtp.length < 4}
                          className={btnCls}
                        >
                          {pending === "verify-reset"
                            ? t("auth.verifying")
                            : t("auth.verify")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setResendTimer(60);
                            doReset({ preventDefault: () => {} } as any);
                          }}
                          disabled={resendTimer > 0 || pending === "resend-reset"}
                          className={ghostBtnCls}
                        >
                          {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                        </button>
                        <button
                          type="button"
                          onClick={() => go("login")}
                          className={ghostBtnCls}
                        >
                          ← {t("auth.backToLogin")}
                        </button>
                      </form>
                    </Panel>
                  )}

                  {/* ── FORGOT NEW PASSWORD ── */}
                  {tabKey === "forgot-new" && (
                    <Panel id="m-forgot-new" dir={1}>
                      <form onSubmit={doResetSubmit} className="space-y-5">
                        <div>
                          <h3 className="text-title-lg font-title-lg text-primary">
                            {t("auth.newPasswordTitle") || "New Password"}
                          </h3>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {t("auth.newPasswordDesc") || "Enter your new password."}
                          </p>
                        </div>
                        <Field label={t("auth.password")} id="mn-pw">
                          <input
                            id="mn-pw"
                            type="password"
                            autoComplete="new-password"
                            placeholder={t("auth.placeholders.createPassword")}
                            className={inputCls}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                        </Field>
                        <button
                          type="submit"
                          disabled={pending === "reset-submit"}
                          className={btnCls}
                        >
                          {pending === "reset-submit"
                            ? t("auth.saving") || "Saving..."
                            : t("auth.savePassword") || "Save Password"}
                        </button>
                        <button
                          type="button"
                          onClick={() => go("login")}
                          className={ghostBtnCls}
                        >
                          ← {t("auth.backToLogin")}
                        </button>
                      </form>
                    </Panel>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
